import classNames from 'classnames';
import * as React from 'react';
import type { AccordionIcon } from '../Types/AccordionIcon';
import type { AccordionColor } from '~/Partials/AccordionComponent/Types/AccordionColor';
import type { AccordionVariant } from '~/Partials/AccordionComponent/Types/AccordionItemVariant';
import type { ButtonProps } from '~/Partials/Button/Button';
import { Button } from '~/Partials/Button/Button';
import { ButtonType } from '~/Partials/Button/ButtonType.csharp';
import { SvgIcon } from '~/Partials/SvgIcon/SvgIcon';
import { assertNever } from '~/Partials/Utils/typeUtils';
import styles from './AccordionExpander.module.css';

export interface BaseAccordionExpanderProps
    extends Required<Pick<ButtonProps, 'aria-expanded' | 'aria-controls' | 'onClick' | 'disabled'>> {
    expanded: boolean;
    variant?: AccordionVariant;
    color?: AccordionColor;
    className?: string;
}

export interface AccordionExpanderProps extends BaseAccordionExpanderProps {
    iconType?: AccordionIcon;
}

export const AccordionExpander: React.FC<React.PropsWithChildren<AccordionExpanderProps>> = props => {
    const {
        children,
        expanded,
        iconType = 'default',
        variant = 'solid',
        color = 'grey',
        className,
        ...otherProps
    } = props;

    const resolveIcon = () => {
        switch (iconType) {
            case 'chevron':
                return expanded ? 'chevron-up' : 'chevron-down';
            case 'default':
                return expanded ? 'minus' : 'plus';
            default:
                assertNever(iconType);
        }
    };

    return (
        <Button
            {...otherProps}
            type={ButtonType.Clear}
            className={classNames(
                styles.AccordionExpander,
                styles[`Variant--${variant}`],
                styles[`Variant--${color}`],
                !expanded && styles['Variant--hidden'],
                className
            )}
        >
            <div className={styles.ButtonContent}>
                <div className={styles.Content}>{children}</div>
                <SvgIcon iconName={resolveIcon()} className={styles.Icon} />
            </div>
        </Button>
    );
};
